import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type {
  ApiKey,
  AuditPage,
  DashboardUserRow,
  EntitlementChange,
  EntitlementPosition,
  Group,
  GroupRef,
  License,
  LicensePage,
  Me,
  Partner,
  PartnerListRow,
  PartnerSummary,
  PriceKind,
  PriceList,
  CreditScaleTier,
  ProvisionResult,
  Role,
  SigningKey,
  SuspensionReason,
  GroupSummary,
  GroupStatement,
  GroupStatementSummary,
  GroupBillingMode,
  BillingDetails,
  Adjustment,
  AdjustmentKind,
  CloseStatusRow,
  InterestRow,
  Statement,
  StatementSummary,
  BulkEnrolResult,
  SmbOwner,
  SmbOwnerPage,
  SmbSite,
  Transaction,
  TransactionReport,
  TransactionType,
  WhitelabelSettings,
  WebhookDelivery,
} from '@shared/types'

/** Admin-editable partner fields — mirrors PartnerBody in functions/_lib/routes/admin.ts. */
export interface PartnerPatch {
  name?: string
  groupId?: string | null
  offers?: Partial<Partner['offers']>
  currency?: string
  language?: string
  billing?: Partial<Partner['billing']>
  contract?: Partial<Omit<NonNullable<Partner['contract']>, 'termEndDate' | 'nonRenewalNoticeDate' | 'priceChangeNoticeDate'>>
  notes?: string
}

export interface PriceItemInput {
  kind: PriceKind
  name: string
  band?: string
  credits?: number | null
  amountMinor: number
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: '/api', credentials: 'include' }),
  tagTypes: ['Me', 'Partners', 'Partner', 'Licenses', 'Summary', 'Users', 'SigningKeys', 'ApiKeys', 'Whitelabel', 'Audit', 'Entitlements', 'PriceList', 'Groups', 'Transactions', 'SmbSites', 'Statements', 'Adjustments', 'Interest'],
  endpoints: (b) => ({
    // --- auth ---
    me: b.query<Me, void>({ query: () => '/auth/me', providesTags: ['Me'] }),
    login: b.mutation<{ ok: true }, { email: string; password: string }>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
    }),
    logout: b.mutation<{ ok: true }, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
    }),
    changePassword: b.mutation<{ ok: true }, { currentPassword?: string; newPassword: string }>({
      query: (body) => ({ url: '/auth/change-password', method: 'POST', body }),
      invalidatesTags: ['Me'],
    }),

    // --- admin ---
    adminPartners: b.query<PartnerListRow[], { archived?: boolean; period?: string } | void>({
      query: (arg) => ({
        url: '/admin/partners',
        params: { ...(arg?.archived ? { archived: '1' } : {}), ...(arg?.period ? { period: arg.period } : {}) },
      }),
      providesTags: ['Partners'],
    }),
    archivePartner: b.mutation<Partner, { slug: string; undo?: boolean }>({
      query: ({ slug, undo }) => ({
        url: `/admin/partners/${slug}/archive`,
        method: 'POST',
        params: undo ? { undo: '1' } : undefined,
      }),
      invalidatesTags: (_r, _e, { slug }) => ['Partners', 'Me', 'Audit', { type: 'Partner', id: slug }],
    }),
    suspendPartner: b.mutation<Partner, { slug: string; reason: SuspensionReason; note: string }>({
      query: ({ slug, ...body }) => ({ url: `/admin/partners/${slug}/suspension`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { slug }) => ['Partners', 'Audit', { type: 'Partner', id: slug }],
    }),
    liftSuspension: b.mutation<Partner, string>({
      query: (slug) => ({ url: `/admin/partners/${slug}/suspension`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, slug) => ['Partners', 'Audit', { type: 'Partner', id: slug }],
    }),
    entitlements: b.query<{ position: EntitlementPosition; changes: EntitlementChange[] }, string>({
      query: (slug) => `/admin/partners/${slug}/entitlements`,
      providesTags: ['Entitlements'],
    }),
    changeEntitlements: b.mutation<EntitlementPosition, { slug: string; to: number }>({
      query: ({ slug, to }) => ({ url: `/admin/partners/${slug}/entitlements`, method: 'POST', body: { to } }),
      invalidatesTags: ['Entitlements', 'Summary', 'Partners', 'Statements', 'Audit'],
    }),
    acknowledgeAutoAdd: b.mutation<{ ok: true }, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/admin/partners/${slug}/entitlements/${id}/acknowledge`, method: 'POST' }),
      invalidatesTags: ['Entitlements', 'Summary', 'Partners', 'Audit'],
    }),
    setPrice: b.mutation<{ ok: true }, { slug: string; sku: string; item: PriceItemInput }>({
      query: ({ slug, sku, item }) => ({ url: `/admin/partners/${slug}/price-list/${sku}`, method: 'PUT', body: item }),
      invalidatesTags: ['PriceList', 'Audit'],
    }),
    setCreditScale: b.mutation<{ ok: true }, { slug: string; tiers: CreditScaleTier[] }>({
      query: ({ slug, tiers }) => ({ url: `/admin/partners/${slug}/credit-scale`, method: 'PUT', body: { tiers } }),
      invalidatesTags: ['PriceList', 'Audit'],
    }),
    removePrice: b.mutation<{ ok: true }, { slug: string; sku: string }>({
      query: ({ slug, sku }) => ({ url: `/admin/partners/${slug}/price-list/${sku}`, method: 'DELETE' }),
      invalidatesTags: ['PriceList', 'Audit'],
    }),
    closeStatus: b.query<CloseStatusRow[], string>({
      query: (period) => ({ url: '/admin/close', params: { period } }),
      providesTags: ['Statements'],
    }),
    closePeriod: b.mutation<{ results: { slug: string; ok: boolean; error?: string }[] }, { period: string; slug?: string }>({
      query: (body) => ({ url: '/admin/close', method: 'POST', body }),
      invalidatesTags: ['Statements', 'Summary', 'Transactions', 'Audit'],
    }),
    adjustments: b.query<Adjustment[], string>({
      query: (slug) => `/admin/partners/${slug}/adjustments`,
      providesTags: ['Adjustments'],
    }),
    addAdjustment: b.mutation<
      { id: string },
      { slug: string; period: string; kind: AdjustmentKind; description: string; amountMinor: number; restatesPeriod?: string | null }
    >({
      query: ({ slug, ...body }) => ({ url: `/admin/partners/${slug}/adjustments`, method: 'POST', body }),
      invalidatesTags: ['Adjustments', 'Statements', 'Audit'],
    }),
    removeAdjustment: b.mutation<{ ok: true }, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/admin/partners/${slug}/adjustments/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Adjustments', 'Statements', 'Audit'],
    }),
    interest: b.query<InterestRow[], void>({ query: () => '/admin/interest', providesTags: ['Interest'] }),
    groups: b.query<Group[], void>({ query: () => '/admin/groups', providesTags: ['Groups'] }),
    createGroup: b.mutation<GroupRef, { name: string }>({
      query: (body) => ({ url: '/admin/groups', method: 'POST', body }),
      invalidatesTags: ['Groups'],
    }),
    partnerAudit: b.query<AuditPage, { slug: string; page?: number }>({
      query: ({ slug, ...params }) => ({ url: `/admin/partners/${slug}/audit`, params }),
      providesTags: ['Audit'],
    }),
    createPartner: b.mutation<Partner, PartnerPatch & { name: string; slug?: string; entitlements: number }>({
      query: (body) => ({ url: '/admin/partners', method: 'POST', body }),
      invalidatesTags: ['Partners', 'Me'],
    }),
    updatePartner: b.mutation<Partner, { slug: string; patch: PartnerPatch }>({
      query: ({ slug, patch }) => ({ url: `/admin/partners/${slug}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_r, _e, { slug }) => ['Partners', 'Me', 'Audit', { type: 'Partner', id: slug }, 'Summary'],
    }),
    adminUsers: b.query<DashboardUserRow[], void>({ query: () => '/admin/users', providesTags: ['Users'] }),
    createUser: b.mutation<
      { user: DashboardUserRow; tempPassword: string },
      { email: string; role: Role; partnerIds?: string[]; groupId?: string }
    >({
      query: (body) => ({ url: '/admin/users', method: 'POST', body }),
      invalidatesTags: ['Users'],
    }),
    updateUser: b.mutation<DashboardUserRow, { id: string; role?: Role; partnerIds?: string[]; groupId?: string | null }>({
      query: ({ id, ...body }) => ({ url: `/admin/users/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Users'],
    }),
    resetUserPassword: b.mutation<{ tempPassword: string }, string>({
      query: (id) => ({ url: `/admin/users/${id}/reset-password`, method: 'POST' }),
      invalidatesTags: ['Users'],
    }),
    deleteUser: b.mutation<{ ok: true }, string>({
      query: (id) => ({ url: `/admin/users/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Users'],
    }),

    // --- partner-scoped ---
    partner: b.query<Partner, string>({
      query: (slug) => `/partners/${slug}`,
      providesTags: (_r, _e, slug) => [{ type: 'Partner', id: slug }],
    }),
    summary: b.query<PartnerSummary, string>({
      query: (slug) => `/partners/${slug}/summary`,
      providesTags: ['Summary'],
    }),
    licenses: b.query<
      LicensePage,
      { slug: string; status?: string; search?: string; from?: string; to?: string; page?: number; sort?: string; dir?: string }
    >({
      query: ({ slug, ...params }) => ({ url: `/partners/${slug}/licenses`, params }),
      providesTags: ['Licenses'],
    }),
    autoAddPreview: b.query<{ autoAdd: { from: number; to: number } | null }, string>({
      query: (slug) => `/partners/${slug}/licenses/auto-add-preview`,
      providesTags: ['Licenses'],
    }),
    provisionLicense: b.mutation<ProvisionResult, { slug: string; label: string }>({
      query: ({ slug, label }) => ({ url: `/partners/${slug}/licenses`, method: 'POST', body: { label } }),
      invalidatesTags: ['Licenses', 'Summary', 'Partners', 'Entitlements', 'Statements', 'Audit'],
    }),
    regenerateLink: b.mutation<ProvisionResult, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/partners/${slug}/licenses/${id}/regenerate`, method: 'POST' }),
      invalidatesTags: ['Licenses', 'Summary', 'Partners', 'Entitlements', 'Statements', 'Audit'],
    }),
    renameLicense: b.mutation<License, { slug: string; id: string; label: string }>({
      query: ({ slug, id, label }) => ({ url: `/partners/${slug}/licenses/${id}`, method: 'PATCH', body: { label } }),
      invalidatesTags: ['Licenses', 'Audit'],
    }),
    revokeLicense: b.mutation<License, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/partners/${slug}/licenses/${id}/revoke`, method: 'POST' }),
      invalidatesTags: ['Licenses', 'Summary', 'Partners', 'Entitlements', 'Audit'],
    }),
    groupSummary: b.query<GroupSummary, string>({
      query: (slug) => `/groups/${slug}/summary`,
      providesTags: ['Summary'],
    }),
    groupStatements: b.query<GroupStatementSummary[], string>({
      query: (slug) => `/groups/${slug}/statements`,
      providesTags: ['Statements'],
    }),
    groupStatement: b.query<GroupStatement, { slug: string; period: string }>({
      query: ({ slug, period }) => `/groups/${slug}/statements/${period}`,
      providesTags: ['Statements', 'Adjustments', 'Transactions'],
    }),
    setGroupBilling: b.mutation<{ ok: true }, { id: string; billingMode: GroupBillingMode; billing: BillingDetails }>({
      query: ({ id, ...body }) => ({ url: `/admin/groups/${id}/billing`, method: 'PUT', body }),
      invalidatesTags: ['Groups', 'Summary', 'Statements', 'Audit'],
    }),
    statements: b.query<StatementSummary[], string>({
      query: (slug) => `/partners/${slug}/statements`,
      providesTags: ['Statements'],
    }),
    statement: b.query<Statement, { slug: string; period: string }>({
      query: ({ slug, period }) => `/partners/${slug}/statements/${period}`,
      providesTags: ['Statements', 'Adjustments', 'Transactions'],
    }),
    smbOwners: b.query<SmbOwnerPage, { slug: string; status?: string; search?: string; removed?: '1'; page?: number }>({
      query: ({ slug, ...params }) => ({ url: `/partners/${slug}/smb-owners`, params }),
      providesTags: ['SmbSites'],
    }),
    enrolSite: b.mutation<{ site: SmbSite; owner: SmbOwner }, { slug: string; siteUrl: string; ownerEmail: string }>({
      query: ({ slug, ...body }) => ({ url: `/partners/${slug}/smb-sites`, method: 'POST', body }),
      invalidatesTags: ['SmbSites', 'Summary', 'Partners', 'Audit'],
    }),
    bulkEnrol: b.mutation<{ results: BulkEnrolResult[] }, { slug: string; rows: { line: number; siteUrl: string; ownerEmail: string }[] }>({
      query: ({ slug, rows }) => ({ url: `/partners/${slug}/smb-sites/bulk`, method: 'POST', body: { rows } }),
      invalidatesTags: ['SmbSites', 'Summary', 'Partners', 'Audit'],
    }),
    removeSite: b.mutation<{ ok: true }, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/partners/${slug}/smb-sites/${id}/remove`, method: 'POST' }),
      invalidatesTags: ['SmbSites', 'Summary', 'Partners', 'Audit'],
    }),
    transactions: b.query<TransactionReport, { slug: string; period: string }>({
      query: ({ slug, period }) => ({ url: `/partners/${slug}/transactions`, params: { period } }),
      providesTags: ['Transactions'],
    }),
    recordTransaction: b.mutation<
      Transaction,
      {
        slug: string
        input: { externalRef: string; type: TransactionType; licenseId?: string; smbOwnerId?: string; smbSiteId?: string; sku?: string; credits?: number; occurredAt?: string; reversesRef?: string }
      }
    >({
      query: ({ slug, input }) => ({ url: `/partners/${slug}/transactions`, method: 'POST', body: input }),
      invalidatesTags: ['Transactions', 'Summary', 'Partners', 'Licenses', 'SmbSites', 'Statements', 'Audit'],
    }),
    retryGrant: b.mutation<Transaction, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/partners/${slug}/transactions/${id}/retry-grant`, method: 'POST' }),
      invalidatesTags: ['Transactions'],
    }),
    priceList: b.query<PriceList, string>({
      query: (slug) => `/partners/${slug}/price-list`,
      providesTags: ['PriceList'],
    }),
    setEmbedOrigins: b.mutation<{ embedOrigins: string[] }, { slug: string; embedOrigins: string[] }>({
      query: ({ slug, embedOrigins }) => ({ url: `/partners/${slug}/embed-origins`, method: 'PUT', body: { embedOrigins } }),
      invalidatesTags: (_r, _e, { slug }) => [{ type: 'Partner', id: slug }, 'Audit'],
    }),
    setReturnDomains: b.mutation<{ returnDomains: string[] }, { slug: string; returnDomains: string[] }>({
      query: ({ slug, returnDomains }) => ({ url: `/partners/${slug}/return-domains`, method: 'PUT', body: { returnDomains } }),
      invalidatesTags: (_r, _e, { slug }) => [{ type: 'Partner', id: slug }, 'Audit'],
    }),
    setWebhook: b.mutation<{ webhookUrl: string | null }, { slug: string; webhookUrl: string | null }>({
      query: ({ slug, webhookUrl }) => ({ url: `/partners/${slug}/webhook`, method: 'PUT', body: { webhookUrl } }),
      invalidatesTags: (_r, _e, { slug }) => [{ type: 'Partner', id: slug }, 'Audit'],
    }),
    webhookDeliveries: b.query<WebhookDelivery[], string>({
      query: (slug) => `/partners/${slug}/webhook-deliveries`,
    }),
    setCheckoutUrl: b.mutation<{ checkoutUrl: string | null }, { slug: string; checkoutUrl: string | null }>({
      query: ({ slug, checkoutUrl }) => ({ url: `/partners/${slug}/checkout-url`, method: 'PUT', body: { checkoutUrl } }),
      invalidatesTags: (_r, _e, { slug }) => [{ type: 'Partner', id: slug }, 'Audit'],
    }),
    setQuotaWarning: b.mutation<{ quotaWarnPct: number | null }, { slug: string; quotaWarnPct: number | null }>({
      query: ({ slug, quotaWarnPct }) => ({ url: `/partners/${slug}/quota-warning`, method: 'PUT', body: { quotaWarnPct } }),
      invalidatesTags: (_r, _e, { slug }) => [{ type: 'Partner', id: slug }, 'Audit'],
    }),
    signingKeys: b.query<SigningKey[], string>({
      query: (slug) => `/partners/${slug}/signing-keys`,
      providesTags: ['SigningKeys'],
    }),
    generateSigningKey: b.mutation<{ id: string; secret: string }, string>({
      query: (slug) => ({ url: `/partners/${slug}/signing-keys`, method: 'POST' }),
      invalidatesTags: ['SigningKeys'],
    }),
    rotateSigningKey: b.mutation<{ id: string; secret: string }, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/partners/${slug}/signing-keys/${id}/rotate`, method: 'POST' }),
      invalidatesTags: ['SigningKeys'],
    }),
    revokeSigningKey: b.mutation<{ ok: true }, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/partners/${slug}/signing-keys/${id}/revoke`, method: 'POST' }),
      invalidatesTags: ['SigningKeys'],
    }),
    apiKeys: b.query<ApiKey[], string>({
      query: (slug) => `/partners/${slug}/api-keys`,
      providesTags: ['ApiKeys'],
    }),
    generateApiKey: b.mutation<{ id: string; key: string }, string>({
      query: (slug) => ({ url: `/partners/${slug}/api-keys`, method: 'POST' }),
      invalidatesTags: ['ApiKeys'],
    }),
    rotateApiKey: b.mutation<{ id: string; key: string }, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/partners/${slug}/api-keys/${id}/rotate`, method: 'POST' }),
      invalidatesTags: ['ApiKeys'],
    }),
    revokeApiKey: b.mutation<{ ok: true }, { slug: string; id: string }>({
      query: ({ slug, id }) => ({ url: `/partners/${slug}/api-keys/${id}/revoke`, method: 'POST' }),
      invalidatesTags: ['ApiKeys'],
    }),

    // --- whitelabel branding ---
    whitelabel: b.query<WhitelabelSettings, string>({
      query: (slug) => `/partners/${slug}/whitelabel`,
      providesTags: ['Whitelabel'],
    }),
    updateWhitelabel: b.mutation<WhitelabelSettings, { slug: string; patch: WhitelabelSettings }>({
      query: ({ slug, patch }) => ({ url: `/partners/${slug}/whitelabel`, method: 'PUT', body: patch }),
      invalidatesTags: ['Whitelabel'],
    }),
    uploadWhitelabelAsset: b.mutation<{ url: string }, { slug: string; kind: 'logo' | 'favicon'; file: File }>({
      // The file is sent as the raw body (fetchBaseQuery passes Blobs through untouched);
      // kind goes in the query string and the image type in the Content-Type header.
      query: ({ slug, kind, file }) => ({
        url: `/partners/${slug}/whitelabel/assets`,
        method: 'POST',
        params: { kind },
        headers: { 'content-type': file.type },
        body: file,
      }),
    }),
  }),
})

export const {
  useMeQuery,
  useLoginMutation,
  useLogoutMutation,
  useChangePasswordMutation,
  useAdminPartnersQuery,
  useArchivePartnerMutation,
  usePartnerAuditQuery,
  useCreatePartnerMutation,
  useUpdatePartnerMutation,
  useAdminUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useResetUserPasswordMutation,
  useDeleteUserMutation,
  useSuspendPartnerMutation,
  useLiftSuspensionMutation,
  useEntitlementsQuery,
  useChangeEntitlementsMutation,
  useAcknowledgeAutoAddMutation,
  useSetPriceMutation,
  useRemovePriceMutation,
  useSetCreditScaleMutation,
  useGroupsQuery,
  useCloseStatusQuery,
  useClosePeriodMutation,
  useAdjustmentsQuery,
  useAddAdjustmentMutation,
  useRemoveAdjustmentMutation,
  useInterestQuery,
  useStatementsQuery,
  useGroupSummaryQuery,
  useGroupStatementsQuery,
  useGroupStatementQuery,
  useSetGroupBillingMutation,
  useStatementQuery,
  useCreateGroupMutation,
  usePartnerQuery,
  useSummaryQuery,
  useLicensesQuery,
  useProvisionLicenseMutation,
  useRegenerateLinkMutation,
  useRenameLicenseMutation,
  useRevokeLicenseMutation,
  useAutoAddPreviewQuery,
  usePriceListQuery,
  useTransactionsQuery,
  useSmbOwnersQuery,
  useEnrolSiteMutation,
  useBulkEnrolMutation,
  useRemoveSiteMutation,
  useRecordTransactionMutation,
  useRetryGrantMutation,
  useSetCheckoutUrlMutation,
  useSetEmbedOriginsMutation,
  useSetQuotaWarningMutation,
  useSetReturnDomainsMutation,
  useSetWebhookMutation,
  useWebhookDeliveriesQuery,
  useSigningKeysQuery,
  useGenerateSigningKeyMutation,
  useRotateSigningKeyMutation,
  useRevokeSigningKeyMutation,
  useApiKeysQuery,
  useGenerateApiKeyMutation,
  useRotateApiKeyMutation,
  useRevokeApiKeyMutation,
  useWhitelabelQuery,
  useUpdateWhitelabelMutation,
  useUploadWhitelabelAssetMutation,
} = api

/**
 * True when a partner-scoped query 404'd. `withPartner` returns 404 both for an
 * unknown slug and for one the caller may not see, so this is a not-found rather
 * than a system fault — worth rendering as such instead of "try again".
 */
export function isNotFound(e: unknown): boolean {
  return (e as { status?: number } | undefined)?.status === 404
}

/** Extract the server's error message from an RTK Query error object. */
export function apiError(e: unknown): string {
  const err = e as { data?: { error?: string } } | undefined
  return err?.data?.error ?? 'Something went wrong. Please try again.'
}
